import React, { useMemo, useState, useEffect, useCallback } from "react";
import Swal from "sweetalert2";
import moment from "moment";
import { useSelector } from "react-redux";
import { hasPermission } from "utils/permissionHelper";
import { PERMISSIONS } from "constants/permissionTypes";
import { MODULES } from "constants/modulesTypes";
import { useNavigate } from "react-router-dom";

//import components
import Breadcrumbs from "../../components/Common/Breadcrumb";
import TableContainer from "../../components/Common/DataTableContainer1";

const DatatableTables = () => {
  const navigate = useNavigate();
  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [countPerPage, setCountPerPage] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [sortingBy, setSortingBy] = useState("Id");
  const [direction, setDirection] = useState("Desc");
  const [search, setSearch] = useState("");
  const token = localStorage.getItem('authToken');
  const user = JSON.parse(localStorage.getItem("authUser"));

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);

      const query = new URLSearchParams({
        page,
        countPerPage,
        sortingBy,
        direction,
        search,
      }).toString();

      const res = await fetch(`https://api.kunalsdrumclass.com/api/GetAllUsers?${query}`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      const result = await res.json();
      const list = result?.recordsets?.[0] || [];
      const total = result?.recordsets?.[1]?.[0]?.TotalRows;
      setTableData(
        list.map((item, index) => ({
          ...item,
          index: (page - 1) * countPerPage + index + 1,
        }))
      );
      setTotalRecords(total);
    } catch (error) {
      console.error("Error loading users:", error);
    } finally {
      setLoading(false);
    }
  }, [page, countPerPage, sortingBy, direction, search]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handlePageChange = (newPage) => setPage(newPage);

  const handlePageSizeChange = (size) => {
    setCountPerPage(size);
    setPage(1);
  };

  const handleSortChange = (columnId, desc) => {
    if (!columnId) {
      setSortingBy(null);
      setDirection("Asc");
    } else {
      setSortingBy(columnId);
      setDirection(desc ? "Desc" : "Asc");
    }
    setPage(1);
  };

  const handleSearch = useCallback((value) => {
    setSearch(value);
    setPage(1);
  }, []);

  const sortingState = useMemo(() => {
    if (!sortingBy) return [];
    return [{ id: sortingBy, desc: direction === "Desc" }];
  }, [sortingBy, direction]);

  const permissionMap = useSelector(
    state => state.permissions?.permissions || {}
  );

  const columns = useMemo(
    () => [
      {
        header: "Id",
        accessorKey: "Id",
        enableSorting: true,
        enableColumnFilter: false,
      },
      {
        header: "First Name",
        meta: {
          sticky: true,
        },
        accessorKey: "FirstName",
        enableSorting: true,
        enableColumnFilter: false,
      },
      {
        header: "Last Name",
        accessorKey: "LastName",
        enableSorting: true,
        enableColumnFilter: false,
      },
      {
        header: "Role",
        accessorKey: "Role",
        enableSorting: true,
        enableColumnFilter: false,
      },
      {
        header: "Email",
        accessorKey: "Email_Address",
        enableSorting: true,
        enableColumnFilter: false,
      },
      {
        header: "Mobile No",
        accessorKey: "Mobile_No",
        enableSorting: true,
        enableColumnFilter: false,
      },
      {
        header: "Status",
        accessorKey: "IsActive",
        enableSorting: true,
        enableColumnFilter: false,
        cell: ({ getValue }) => {
          const isActive = getValue() === 1;

          return (
            <span
              className={`badge ${isActive ? "bg-success" : "bg-danger"
                }`}
            >
              {isActive ? "Active" : "Inactive"}
            </span>
          );
        },
      },
      {
        header: "Created Date",
        accessorKey: "CreatedOn",
        enableSorting: true,
        enableColumnFilter: false,
        cell: (info) => moment(info.getValue()).format("DD-MM-YYYY"),
      },
      {
        header: "Action",
        enableSorting: false,
        enableColumnFilter: false,
        cell: ({ row }) => {
          const rowData = row.original;
          const isSuperAdminRecord = rowData?.Role === "Super Admin" && user.Role != "Super Admin";

          return (
            <div className="d-flex gap-2">
              {hasPermission(permissionMap, MODULES.USERS, PERMISSIONS.VIEW) && (
                <button className="btn btn-sm btn-primary" disabled={isSuperAdminRecord} onClick={() => navigate(`/user/view/${rowData.Id}`)}>View</button>
              )}
              {hasPermission(permissionMap, MODULES.USERS, PERMISSIONS.EDIT) && (
                <button className="btn btn-sm btn-secondary" disabled={isSuperAdminRecord} onClick={() => navigate(`/user/edit/${rowData.Id}`)}>Edit</button>
              )}
              {hasPermission(permissionMap, MODULES.USERS, PERMISSIONS.DELETE) && (
                <button className="btn btn-sm btn-danger" disabled={isSuperAdminRecord} onClick={() => handleDelete(rowData)}>Delete</button>
              )}
              {hasPermission(permissionMap, MODULES.USERPERMISSION, PERMISSIONS.VIEW) && (
                <button className="btn btn-sm btn-success" disabled={isSuperAdminRecord} onClick={() => navigate(`/user-permission/${rowData.Id}`)}>User Permission</button>
              )}
            </div>
          );
        },
      }
    ],
    []
  );

  const handleDelete = async (rowData) => {
    const result = await Swal.fire({
      title: "Warning!",
      text: `Are you sure you want to delete this user?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, continue",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#dc3545",
      customClass: {
        popup: "swal2-material-ui",
        confirmButton: "delete-alert",
        cancelButton: "cancel-alert",
      },
    });

    if (!result.isConfirmed) return;
    try {
      const payload = { id: rowData.Id, modifiedBy: user.Id };
      await fetch(`https://api.kunalsdrumclass.com/api/DeleteUserById?id=${payload.id}&modifiedBy=${payload.modifiedBy}`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      await Swal.fire({
        title: "Success!",
        text: `user deleted successfully!`,
        icon: "success",
        customClass: {
          popup: "swal2-material-ui",
          confirmButton: "btn btn-sm rounded-8 btn-primary",
        },
      });
      fetchData();
    } catch (error) {
      await Swal.fire({
        title: "Error!",
        text: "Something went wrong!",
        icon: "error",
        confirmButtonColor: "#d32f2f",
        customClass: {
          popup: "swal2-material-ui",
          confirmButton: "error-alert",
        },
      });
    }
  };

  //meta title
  document.title = "Users | KDC - Admin";

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumbs title="Users"
          breadcrumbItems={[
            { title: "Users" }
          ]}
        />

        {hasPermission(permissionMap, MODULES.USERS, PERMISSIONS.INSERT) && (
          <div className="d-flex justify-content-end mb-3">
            <button className="btn btn-primary mb-3" onClick={() => navigate("/user/create")}>
              + Create User
            </button>
          </div>
        )}

        <TableContainer
          columns={columns}
          data={tableData}
          page={page}
          countPerPage={countPerPage}
          totalRecords={totalRecords}
          sortingState={sortingState}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          onSortChange={handleSortChange}
          searchValue={search}
          onSearch={handleSearch}
          isLoading={loading}
          isPagination
          isGlobalFilter
          isCustomPageSize
          SearchPlaceholder="search..."
          pagination="pagination"
          paginationWrapper='dataTables_paginate paging_simple_numbers'
          tableClass="table-bordered table-nowrap dt-responsive nowrap w-100 dataTable no-footer dtr-inline"
        />

      </div>
    </div>
  );
};

export default DatatableTables;
